/**
 * React Query hooks for notification preferences
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { notificationKeys } from './useNotifications';
import {
  getCompanyNotificationPreferences,
  upsertCompanyNotificationPreference,
  batchUpdateCompanyPreferences,
  getUserNotificationPreferences,
  upsertUserNotificationPreference,
  getUserMutes,
  addMute,
  removeMute,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
  getResolvedUserPreferences,
  type CompanyNotificationPreference,
  type UpdatePreferenceInput,
  type UserNotificationPreference,
  type NotificationMute,
  type ResolvedNotificationPreference,
} from '@/services/notifications';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const notificationPreferenceKeys = {
  all: ['notification-preferences'] as const,
  company: (companyId: string) => [...notificationPreferenceKeys.all, 'company', companyId] as const,
  user: (profileId: string, companyId: string) => [...notificationPreferenceKeys.all, 'user', profileId, companyId] as const,
  resolved: () => [...notificationPreferenceKeys.all, 'resolved', 'self'] as const,
  mutes: (profileId: string, companyId: string) => [...notificationPreferenceKeys.all, 'mutes', profileId, companyId] as const,
  unreadCount: () => [...notificationPreferenceKeys.all, 'unread-count'] as const,
};

// ============================================================================
// RESOLVED PREFERENCES HOOK (Role + Wildcard Support)
// ============================================================================

/**
 * Hook to get fully resolved notification preferences for the current authenticated user.
 * This handles role-level inheritance, wildcard matching, and force-enabled.
 * 
 * SECURITY: The RPC always uses auth.uid() - no cross-user access is possible.
 */
export function useResolvedUserNotificationPreferences() {
  return useQuery({
    queryKey: notificationPreferenceKeys.resolved(),
    queryFn: () => getResolvedUserPreferences(),
  });
}

// ============================================================================
// COMPANY PREFERENCES HOOKS
// ============================================================================

export function useCompanyNotificationPreferences(companyId: string | undefined) {
  return useQuery({
    queryKey: notificationPreferenceKeys.company(companyId || ''),
    queryFn: () => getCompanyNotificationPreferences(companyId!),
    enabled: !!companyId,
  });
}

export function useUpdateCompanyPreference(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (preference: UpdatePreferenceInput) => {
      if (!companyId) throw new Error('Company ID required');
      return upsertCompanyNotificationPreference(companyId, preference);
    },
    // Optimistic update for instant UI feedback
    onMutate: async (newPreference) => {
      if (!companyId) return;
      
      const queryKey = notificationPreferenceKeys.company(companyId);
      
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot previous value for rollback
      const previousPrefs = queryClient.getQueryData<CompanyNotificationPreference[]>(queryKey);
      
      // Optimistically update the cache
      queryClient.setQueryData<CompanyNotificationPreference[]>(queryKey, (old) => {
        if (!old) {
          return [{
            type_key: newPreference.type_key,
            enabled: newPreference.enabled ?? true,
            channels: newPreference.channels ?? ['in_app'],
            force_enabled: newPreference.force_enabled ?? false,
          }] as CompanyNotificationPreference[];
        }
        
        const existingIndex = old.findIndex(p => p.type_key === newPreference.type_key);
        if (existingIndex >= 0) {
          const updated = [...old];
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...newPreference,
          };
          return updated;
        }
        
        return [...old, {
          type_key: newPreference.type_key,
          enabled: newPreference.enabled ?? true,
          channels: newPreference.channels ?? ['in_app'],
          force_enabled: newPreference.force_enabled ?? false,
        }] as CompanyNotificationPreference[];
      });
      
      return { previousPrefs };
    },
    onError: (error: Error, _variables, context) => {
      // Rollback on error
      if (companyId && context?.previousPrefs) {
        queryClient.setQueryData(
          notificationPreferenceKeys.company(companyId),
          context.previousPrefs
        );
      }
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Preference updated',
        description: 'Organization notification preference has been saved.',
      });
    },
    onSettled: () => {
      // Always refetch after mutation settles to ensure server state
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.company(companyId) });
      }
    },
  });
}

export function useBatchUpdateCompanyPreferences(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (preferences: UpdatePreferenceInput[]) => {
      if (!companyId) throw new Error('Company ID required');
      return batchUpdateCompanyPreferences(companyId, preferences);
    },
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.company(companyId) });
      }
      toast({
        title: 'Preferences saved',
        description: 'All notification preferences have been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Save failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// USER PREFERENCES HOOKS
// ============================================================================

export function useUserNotificationPreferences(profileId: string | undefined, companyId: string | undefined) {
  return useQuery({
    queryKey: notificationPreferenceKeys.user(profileId || '', companyId || ''),
    queryFn: () => getUserNotificationPreferences(profileId!, companyId!),
    enabled: !!profileId && !!companyId,
  });
}

export function useUpdateUserPreference(profileId: string | undefined, companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (preference: { type_key: string; enabled?: boolean; channels?: string[] }) => {
      if (!profileId || !companyId) throw new Error('Profile and Company ID required');
      return upsertUserNotificationPreference(profileId, companyId, preference);
    },
    // Optimistic update: immediately reflect the change in UI
    onMutate: async (newPreference) => {
      if (!profileId || !companyId) return;
      
      const queryKey = notificationPreferenceKeys.user(profileId, companyId);
      
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot previous value for rollback
      const previousPrefs = queryClient.getQueryData<UserNotificationPreference[]>(queryKey);
      
      // Optimistically update the cache
      queryClient.setQueryData<UserNotificationPreference[]>(queryKey, (old) => {
        if (!old) {
          // No existing data - create new entry
          return [{
            type_key: newPreference.type_key,
            enabled: newPreference.enabled === true || newPreference.enabled === false 
              ? newPreference.enabled 
              : true,
            channels: newPreference.channels ?? ['in_app'],
          }];
        }
        
        const existingIndex = old.findIndex(p => p.type_key === newPreference.type_key);
        if (existingIndex >= 0) {
          // Update existing preference
          const updated = [...old];
          updated[existingIndex] = {
            ...updated[existingIndex],
            // Explicitly handle boolean to avoid falsy coercion issues
            enabled: newPreference.enabled === true || newPreference.enabled === false 
              ? newPreference.enabled 
              : updated[existingIndex].enabled,
            channels: newPreference.channels ?? updated[existingIndex].channels,
          };
          return updated;
        } else {
          // Add new preference
          return [...old, {
            type_key: newPreference.type_key,
            enabled: newPreference.enabled === true || newPreference.enabled === false 
              ? newPreference.enabled 
              : true,
            channels: newPreference.channels ?? ['in_app'],
          }];
        }
      });
      
      // Return context with previous data for rollback
      return { previousPrefs, queryKey };
    },
    onError: (error: Error, _newPreference, context) => {
      // Rollback to previous state on error
      if (context?.previousPrefs !== undefined && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousPrefs);
      }
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Preference updated',
        description: 'Your notification preference has been saved.',
      });
    },
    onSettled: () => {
      // Always refetch after mutation settles to ensure server state consistency
      if (profileId && companyId) {
        queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.user(profileId, companyId) });
        // Also invalidate resolved preferences since MyNotificationsCard reads from this cache
        queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.resolved() });
      }
    },
  });
}

// ============================================================================
// MUTES HOOKS
// ============================================================================

export function useNotificationMutes(profileId: string | undefined, companyId: string | undefined) {
  return useQuery({
    queryKey: notificationPreferenceKeys.mutes(profileId || '', companyId || ''),
    queryFn: () => getUserMutes(profileId!, companyId!),
    enabled: !!profileId && !!companyId,
  });
}

export function useAddMute(profileId: string | undefined, companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ typeKey, expiresAt }: { typeKey: string; expiresAt?: string }) => {
      if (!profileId || !companyId) throw new Error('Profile and Company ID required');
      return addMute(profileId, companyId, typeKey, expiresAt);
    },
    onSuccess: () => {
      if (profileId && companyId) {
        queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.mutes(profileId, companyId) });
        // Also invalidate resolved preferences since mutes affect resolved state
        queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.resolved() });
      }
      toast({
        title: 'Notification muted',
        description: 'You will no longer receive this notification type.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Mute failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveMute(profileId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (typeKey: string) => {
      if (!profileId) throw new Error('Profile ID required');
      return removeMute(profileId, typeKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.all });
      toast({
        title: 'Notification unmuted',
        description: 'You will now receive this notification type.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Unmute failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// NOTIFICATION COUNT HOOKS
// ============================================================================

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationPreferenceKeys.unreadCount(),
    queryFn: getUnreadCount,
    // Removed polling - now using Supabase Realtime subscription
    // See: NotificationRealtimeProvider for subscription setup
    // F3/F6 fallback: Still refetch on window focus
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      // Invalidate both list and count to ensure UI updates immediately
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.unreadCount() });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not mark as read', description: error.message, variant: 'destructive' });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: (count) => {
      // Invalidate both list and count to ensure UI updates immediately
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.unreadCount() });
      toast({
        title: 'All read',
        description: `Marked ${count} notifications as read.`,
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not mark all as read', description: error.message, variant: 'destructive' });
    },
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: dismissNotification,
    onSuccess: () => {
      // Invalidate both list and count to ensure UI updates immediately
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.unreadCount() });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not dismiss notification', description: error.message, variant: 'destructive' });
    },
  });
}
